import { useRouter } from "next/router";
import styles from "./EventCard.module.scss";
import { format, parse } from "date-fns";
import { LinkType } from "../../types/baseTypes";
import { customSanitize } from "../../utils/customSanitize";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { formatISO } from "date-fns";
import { ContentTag } from "../ContentTag/ContentTag";
import { Modul_Termin_Grid } from "@/wp/types/acf-module-types";
import { Button, ButtonTypes } from "../Button/Button";
import { isBefore } from "date-fns";

interface EventCardProps {
  headlineTag: Modul_Termin_Grid["events_grid"]["headline_html_tag"];
  element: EventCardType;
  show_border_radius: boolean;
  show_box_shadow: boolean;
  showBox: boolean;
  moduleType: "events-slider" | "events-grid";
  skeleton?: boolean;
}

export type EventCardType = {
  date: string;
  location?: {
    city: string;
    place: string;
    street: string;
    zip: string;
    link: LinkType | null;
  };
  start_time?: string;
  end_time?: string;
  title?: string;
  excerpt?: string;
  link: string;
};

const truncateExcerpt = (excerpt: string, maxLength: number) => {
  if (!excerpt) return "";

  // If the excerpt is shorter than the max length, return it as is
  if (excerpt.length <= maxLength) return excerpt;

  // Find the last space within the max length
  const truncated = excerpt.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  // If there's no space within the max length, return truncated string with ellipsis
  if (lastSpaceIndex === -1) return truncated + "...";

  // Otherwise, return truncated string up to the last space with ellipsis
  return truncated.slice(0, lastSpaceIndex) + "...";
};

// Function to convert 12-hour format to 24-hour format
const convert12To24Hour = (time: string | undefined) => {
  if (!time) return undefined;

  // Parse the time assuming it's in 'h:mm a' (12-hour) format (e.g., 2:30 PM)
  const parsedTime = parse(time, "h:mm a", new Date());

  // Format it to 24-hour format (e.g., 14:30)
  return format(parsedTime, "HH:mm");
};

export const EventCard = ({
  headlineTag,
  element,
  show_border_radius,
  show_box_shadow,
  showBox,
  moduleType,
  skeleton = false,
}: EventCardProps) => {
  const { locale } = useRouter();
  const { getLabel } = useGlobalContext();
  const link = element.link;

  const eventDateObj = new Date(element.date.split("/").reverse().join("-"));

  const today = new Date();
  const isOldEvent = isBefore(eventDateObj, today);

  const readMoreLabel = getLabel("read_more_label");
  const startTimeLabel = getLabel("start_time");
  const endTimeLabel = getLabel("end_time");

  const start_time = element.start_time
    ? convert12To24Hour(element.start_time)
    : undefined;
  const end_time = element.end_time
    ? convert12To24Hour(element.end_time)
    : undefined;

  const excerpt = element.excerpt
    ? truncateExcerpt(element.excerpt || "", 256)
    : undefined;

  // format date to ISO 8601 format
  const eventDate = formatISO(eventDateObj);

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const formatTime = () => (
    <section className={`${styles["embla-slide-time"]} date`}>
      <time dateTime={start_time} aria-label={startTimeLabel}>
        {start_time}
      </time>

      {end_time && (
        <p>
          -{" "}
          <time dateTime={end_time} aria-label={endTimeLabel}>
            {end_time}
          </time>
        </p>
      )}

      {locale === "de" && <p>Uhr</p>}
    </section>
  );

  return (
    <div
      data-module-type={`${moduleType}-${isOldEvent ? "old" : "new"}`}
      className={`${styles["embla-slide"]} embla-slide ${showBox ? "is-box" : ""}
    ${show_border_radius ? "has-border-radius" : ""} ${show_box_shadow ? "has-box-shadow" : ""}
    `}
    >
      <div className={`${styles["embla-slide-inner"]}`}>
        <article className={styles["embla-slide-inner-content"]}>
          <time
            dateTime={eventDate}
            className={`${styles["embla-slide-date"]} date`}
          >
            <span className={`block ${skeletonElementClass}`}>
              {new Date(element.date).toLocaleDateString(locale, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </span>
          </time>

          {element.location && (
            <address
              className={`location ${styles["embla-slide-location"]} ${skeletonElementClass}`}
            >
              {`${element.location.place}:`}
              <span className={styles["separator"]} />
              {`${element.location.street},`}
              <span className={styles["separator"]} />
              {`${element.location.zip},`}
              <span className={styles["separator"]} />
              {`${element.location.city}`}
            </address>
          )}

          {start_time ? formatTime() : null}

          {element.title && (
            <span
              role="heading"
              aria-level={headlineTag === "h3" ? 4 : 3}
              className={`${styles["embla-slide-title"]} title has-text-left ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.title),
              }}
            />
          )}

          {excerpt && (
            <ContentTag
              content={excerpt}
              extraClass={`${skeletonElementClass}`}
            />
          )}
        </article>

        <Button
          type="secondary"
          href={link}
          className={`${skeletonElementClass}`}
          target="_self"
          aria-label={`${readMoreLabel}: ${element.title}`}
          disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
        >
          {readMoreLabel}
        </Button>
      </div>
    </div>
  );
};

interface EndCardProps {
  headlineTag: Modul_Termin_Grid["events_grid"]["headline_html_tag"];
  end_card: {
    background_color: string | false;
    button_type: ButtonTypes;
    link: LinkType | false;
    title: string;
  };
  show_border_radius: boolean;
  show_box_shadow: boolean;
  showBox: boolean;
}

export const EndCard = ({
  end_card,
  show_border_radius,
  show_box_shadow,
  showBox,
  headlineTag,
}: EndCardProps) => {
  const { background_color, button_type, title, link } = end_card;
  if (!link || !link.url) return null;
  return (
    <article
      style={{ backgroundColor: `var(--${background_color})` }}
      className={`${styles["embla-slide"]} ${show_border_radius && showBox ? "has-border-radius" : ""} ${show_box_shadow && showBox ? "has-box-shadow" : ""} end-card embla-slide is-flex is-flex-direction-column is-justify-content-center is-align-items-center`}
    >
      {title && (
        <span
          role="heading"
          aria-level={headlineTag === "h3" ? 4 : 3}
          className="end-card-title mb-4"
        >
          {title}
        </span>
      )}
      {link && (
        <Button
          href={link.url}
          target="_self"
          className="my-0"
          type={button_type ?? "secondary"}
          aria-label={link.aria_label}
        >
          {link.title}
        </Button>
      )}
    </article>
  );
};
